import { parseLocalDate } from '../../../domain/utils/local-date';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import { HolidayService } from '../../../domain/services/holidays/holiday.service';
import { HolidayModel } from '../../../domain/models/holiday.model';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTH_ABBR = [
  'Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.',
  'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.',
];

const ORDINAL_SUFFIX: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };

function formatOrdinalDay(day: number): string {
  const suffix = ORDINAL_SUFFIX[day] ?? 'th';
  return `${day}${suffix}`;
}

interface HolidayRow {
  id: number;
  name: string;
  dateDisplay: string;
  dayOfWeekDisplay: string;
}

function currentYear(): number {
  return new Date().getFullYear();
}

function resolveConfiguredDate(h: HolidayModel, year: number): Date | null {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const monthIndex = monthNames.indexOf(h.month ?? '');
  if (monthIndex === -1) return null;

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const targetDay = dayNames.indexOf(h.dayOfWeek ?? 'Day');

  if (h.ordinal === 'Last') {
    // Last occurrence of the day in the month
    const lastDay = new Date(year, monthIndex + 1, 0);
    let d = lastDay.getDate();
    if (targetDay === -1) return lastDay; // "Day" = last day of month
    while (lastDay.getDay() !== targetDay) {
      lastDay.setDate(--d);
    }
    return lastDay;
  }

  const ordinalMap: Record<string, number> = {
    First: 1, Second: 2, Third: 3, Fourth: 4,
  };
  const occurrence = ordinalMap[h.ordinal ?? 'First'] ?? 1;

  if (targetDay === -1) {
    // "Day" ordinal: nth day of month
    return new Date(year, monthIndex, occurrence);
  }

  // Find the nth occurrence of targetDay in the month
  const first = new Date(year, monthIndex, 1);
  const firstDow = first.getDay();
  let offset = (targetDay - firstDow + 7) % 7;
  const date = new Date(year, monthIndex, 1 + offset + (occurrence - 1) * 7);
  return date;
}

@Component({
  selector: 'app-holidays',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSortModule,
    MatSelectModule,
    MatFormFieldModule,
  ],
  templateUrl: './holidays.component.html',
  styleUrl: './holidays.component.css',
})
export class HolidaysComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<HolidayRow>;

  displayedColumns = ['name', 'date', 'dayOfWeek', 'actions'];
  dataSource = new MatTableDataSource<HolidayRow>([]);

  selectedYear: number = currentYear();
  years: number[] = [];

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly holidayService: HolidayService,
    private readonly dialogService: DialogService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/admin/holidays', label: 'Administrative' },
      { icon: 'public', url: '/app/admin/holidays', label: 'Holidays' },
    ]);

    const base = currentYear();
    for (let y = base - 2; y <= base + 2; y++) {
      this.years.push(y);
    }

    this.loadHolidays();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  loadHolidays(): void {
    // ponytail: backend GET /holiday ignores the `year` query param entirely (returns
    // every holiday regardless of year), so "specific date" holidays from other years
    // never dropped out of the list and the Year selector appeared to do nothing.
    // Configured (recurring) holidays have no fixed year, so they always match.
    this.holidayService.getHolidays(this.selectedYear).subscribe((holidays) => {
      const forYear = holidays.filter((h) => this.matchesSelectedYear(h));
      this.dataSource.data = forYear.map((h) => this.toRow(h));
    });
  }

  private matchesSelectedYear(h: HolidayModel): boolean {
    if (h.type === 'specific' && h.date) {
      return parseLocalDate(h.date as any)!.getFullYear() === this.selectedYear;
    }
    return true;
  }

  onYearChange(): void {
    this.loadHolidays();
  }

  toRow(h: HolidayModel): HolidayRow {
    let dateDisplay = '';
    let dayOfWeekDisplay = '';

    if (h.type === 'specific' && h.date) {
      const d = parseLocalDate(h.date as any)!;
      const month = MONTH_ABBR[d.getMonth()];
      const day = formatOrdinalDay(d.getDate());
      dateDisplay = `${month} ${day}`;
      dayOfWeekDisplay = DAY_NAMES[d.getDay()];
    } else {
      // Configured: resolve the actual date for the selected year
      const resolved = resolveConfiguredDate(h, this.selectedYear);
      if (resolved) {
        const month = MONTH_ABBR[resolved.getMonth()];
        const day = formatOrdinalDay(resolved.getDate());
        dateDisplay = `${month} ${day}`;
        dayOfWeekDisplay = DAY_NAMES[resolved.getDay()];
      } else {
        // Fallback: show ordinal description
        dateDisplay = `${h.ordinal} ${h.dayOfWeek} of ${h.month}`;
        dayOfWeekDisplay = h.dayOfWeek ?? '';
      }
    }

    return { id: h.id, name: h.name, dateDisplay, dayOfWeekDisplay };
  }

  onAddHoliday(): void {
    this.router.navigate(['/app/admin/holidays/new']);
  }

  onEdit(row: HolidayRow): void {
    this.router.navigate(['/app/admin/holidays', row.id]);
  }

  onDelete(row: HolidayRow): void {
    this.dialogService.openDeletedDialog(
      'Delete Holiday',
      `Are you sure you want to delete "${row.name}"?`,
      (confirmed: boolean) => {
        if (confirmed) {
          this.holidayService.deleteHoliday(row.id).subscribe(() => {
            this.loadHolidays();
          });
        }
      }
    );
  }

  goBack(): void {
    window.history.back();
  }
}
